import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import { Link } from "react-router-dom";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Tooltip, Legend } from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import "../styles/global.css";
import "../styles/Dashboard.css";
import { apiPost } from "../utils/api";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Tooltip, Legend);

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalMessages: 5240,
    successfulMessages: 4890,
    failedMessages: 350,
    pendingMessages: 45,
    totalChats: 128,
    activeChats: 54,
  });

  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const resp = await apiPost("/dashboard/GetOverview", {});

        // set stats
        setStats((s) => ({
          ...s,
          totalMessages: resp.totalMessages || 0,
          successfulMessages: resp.successfulMessages || 0,
          failedMessages: resp.failedMessages || 0,
          pendingMessages: resp.pendingMessages || 0,
          totalChats: resp.totalChats || 0,
          activeChats: resp.activeChats || 0,
        }));

        // messagesTrend -> chart
        const trendLabels = (resp.messagesTrend || []).map((d) => d.date);
        const trendData = (resp.messagesTrend || []).map((d) => d.count);

        // status
        const statusLabels = ["Successful", "Failed", "Pending"];
        const statusData = [
          resp.messageStatus?.successful || 0,
          resp.messageStatus?.failed || 0,
          resp.messageStatus?.pending || 0,
        ];

        // hourly
        const hourlyLabels = (resp.hourlyMessages || []).map((h) => h.label);
        const hourlyData = (resp.hourlyMessages || []).map((h) => h.count);

        setChartData({
          messagesTrend: {
            labels: trendLabels,
            datasets: [
              {
                label: "Messages Sent",
                data: trendData,
                borderColor: "#2e7d32",
                backgroundColor: "rgba(46, 125, 50, 0.1)",
                borderWidth: 2,
                tension: 0.4,
                fill: true,
              },
            ],
          },
          messageStatus: {
            labels: statusLabels,
            datasets: [
              {
                data: statusData,
                backgroundColor: ["#4CAF50", "#f44336", "#FFC107"],
                borderColor: ["#388E3C", "#C62828", "#FFA000"],
                borderWidth: 2,
              },
            ],
          },
          hourlyMessages: {
            labels: hourlyLabels,
            datasets: [
              {
                label: "Messages by Hour",
                data: hourlyData,
                backgroundColor: "#1976D2",
                borderColor: "#0D47A1",
                borderWidth: 1,
              },
            ],
          },
        });
      } catch (err) {
        console.error("Failed to load dashboard data", err);
      }
    };

    fetchDashboard();
  }, []);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: {
          padding: 15,
          font: {
            size: 12,
            weight: "500",
          },
        },
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: 12,
        titleFont: { size: 14 },
        bodyFont: { size: 12 },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          padding: 10,
        },
      },
      x: {
        ticks: {
          padding: 10,
        },
      },
    },
  };

  const StatCard = ({ title, value, subtitle, icon, color }) => (
    <div className="stat-card" style={{ borderLeftColor: color }}>
      <div className="stat-icon" style={{ backgroundColor: color + "20" }}>
        {icon}
      </div>
      <div className="stat-content">
        <h3 className="stat-title">{title}</h3>
        <p className="stat-value">{value}</p>
        {subtitle && <p className="stat-subtitle">{subtitle}</p>}
      </div>
    </div>
  );

  return (
    <>
      <Navbar />
      <div className="page-container">
        {/* Header Section */}
        <div className="dashboard-header">
          <div className="header-content">
            <h1>Dashboard</h1>
            <p>Welcome back! Here's your messaging overview</p>
          </div>
          <div className="header-actions">
            <Link to="/send-message" className="primary-btn">
              📨 Send Message
            </Link>
            <Link to="/chat" className="secondary-btn">
              💬 Open Chat
            </Link>
          </div>
        </div>

        {/* Statistics Section */}
        <section className="stats-section">
          <h2 className="section-title">Key Metrics</h2>
          <div className="stats-grid">
            <StatCard
              title="Total Messages"
              value={stats.totalMessages.toLocaleString()}
              subtitle="All time"
              icon="📊"
              color="#4CAF50"
            />
            <StatCard
              title="Successful"
              value={stats.successfulMessages.toLocaleString()}
              subtitle={`${((stats.successfulMessages / stats.totalMessages) * 100).toFixed(1)}% success rate`}
              icon="✅"
              color="#2196F3"
            />
            <StatCard
              title="Failed"
              value={stats.failedMessages.toLocaleString()}
              subtitle={`${((stats.failedMessages / stats.totalMessages) * 100).toFixed(1)}% failure rate`}
              icon="❌"
              color="#f44336"
            />
            <StatCard
              title="Pending"
              value={stats.pendingMessages}
              subtitle="In queue"
              icon="⏳"
              color="#FF9800"
            />
            <StatCard
              title="Total Chats"
              value={stats.totalChats}
              subtitle={`${stats.activeChats} active`}
              icon="💬"
              color="#9C27B0"
            />
            
          </div>
        </section>

        {/* Charts Section */}
        <section className="charts-section">
          <div className="chart-grid">
            {/* Messages Trend */}
            <div className="chart-card">
              <h3 className="chart-title">Weekly Message Trend</h3>
              {chartData && (
                <Line
                  data={chartData.messagesTrend}
                  options={{
                    ...chartOptions,
                    scales: {
                      ...chartOptions.scales,
                      y: {
                        ...chartOptions.scales.y,
                        title: { display: true, text: "Number of Messages" },
                      },
                    },
                  }}
                />
              )}
            </div>

            {/* Message Status */}
            <div className="chart-card">
              <h3 className="chart-title">Message Status Distribution</h3>
              {chartData && (
                <div className="chart-container-centered">
                  <Doughnut
                    data={chartData.messageStatus}
                    options={{
                      ...chartOptions,
                      plugins: {
                        ...chartOptions.plugins,
                        legend: {
                          ...chartOptions.plugins.legend,
                          position: "right",
                        },
                      },
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Hourly Distribution */}
          <div className="chart-card full-width">
            <h3 className="chart-title">Hourly Message Distribution</h3>
            {chartData && (
              <Bar
                data={chartData.hourlyMessages}
                options={{
                  ...chartOptions,
                  scales: {
                    ...chartOptions.scales,
                    y: {
                      ...chartOptions.scales.y,
                      title: { display: true, text: "Number of Messages" },
                    },
                  },
                }}
              />
            )}
          </div>
        </section>

        {/* Quick Actions Section */}
        <section className="quick-actions-section">
          <h2 className="section-title">Quick Actions</h2>
          <div className="actions-grid">
            <Link to="/send-message" className="action-card">
              <span className="action-icon">📨</span>
              <span className="action-text">Send New Message</span>
              <span className="action-arrow">→</span>
            </Link>
            <Link to="/chat" className="action-card">
              <span className="action-icon">💬</span>
              <span className="action-text">View Conversations</span>
              <span className="action-arrow">→</span>
            </Link>
            <Link to="/reports/messages" className="action-card">
              <span className="action-icon">📊</span>
              <span className="action-text">View Reports</span>
              <span className="action-arrow">→</span>
            </Link>
            <div className="action-card" style={{ opacity: 0.6, cursor: "not-allowed" }}>
              <span className="action-icon">⚙️</span>
              <span className="action-text">Settings (Coming Soon)</span>
              <span className="action-arrow">→</span>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
